import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { FaGlobe, FaLock, FaEyeSlash, FaTools, FaPowerOff } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./App_visibility.css";

// Cards shown in this panel:
//   maintenanceOnly: true -> the whole Inside D portal; only a Live/Maintenance
//                    switch applies (it isn't a role-gated launcher tile).
//   roles: null   -> portal shows the tile to everyone; a visitor sees it, so
//                    "Visitors" is meaningful.
//   roles: [...]  -> portal restricts the tile to those signed-in roles and NEVER
//                    shows it to a visitor, so "Visitors" does not apply.
// Colours match each app's left-navbar section colour.
const APPS = [
    { name: "Inside D", color: "#bcd651", roles: null, maintenanceOnly: true },
    { name: "dAdmin",  color: "#51acd6", roles: ["Admin", "Sub Admin"], noMaintenance: true },
    { name: "dEpr",    color: "#d68d51", roles: ["Admin"] },
    { name: "dAssist", color: "#51d69f", roles: null },
    { name: "dBug",    color: "#d65184", roles: null },
    { name: "dTime",   color: "#5184d6", roles: null },
    { name: "dSlip",   color: "#d66c51", roles: null },
    { name: "dForms",  color: "#7462da", roles: null },
];

// Visibility modes are mutually exclusive (pick one). Maintenance is a SEPARATE
// switch that layers on top of any of them - so a card can show e.g. "Visitors"
// AND "Maintenance" active at the same time.
const VIS_MODES = [
    { value: "visitors",  label: "Visitors",      icon: FaGlobe,    hint: "Anyone (visitors + employees) can open it." },
    { value: "employees", label: "Employee only", icon: FaLock,     hint: "Login required - hidden from visitors." },
    { value: "hidden",    label: "Hidden",        icon: FaEyeSlash, hint: "Removed for everyone, including Admins." },
];

// Public tiles start visitor-visible; role-restricted tiles start employee-only.
const defaultVisFor = (app) => (Array.isArray(app.roles) ? "employees" : "visitors");
const labelOf = (mode) => VIS_MODES.find((m) => m.value === mode)?.label || mode;

const App_visibility = ({ navSize }) => {
    const navigate = useNavigate();
    const [apps, setApps] = useState({});      // { dAssist: { mode, maintenance }, ... }
    const [loading, setLoading] = useState(true);
    const [savingApp, setSavingApp] = useState(null);

    useEffect(() => {
        fetchloginedEmployees();
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") navigate("/login");
        } catch (e) { console.error("Error fetching employee:", e); }
    };

    const load = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/api/app-visibility`);
            const data = await res.json();
            setApps(data?.apps || {});
        } catch (e) {
            console.error("❌ app-visibility load:", e);
        } finally { setLoading(false); }
    };

    // Shared writer: POST a partial change ({mode} or {maintenance}), optimistic
    // with revert on error.
    const patch = async (app, body, optimistic, okMsg) => {
        const prev = apps[app];
        setSavingApp(app);
        setApps((p) => ({ ...p, [app]: { ...p[app], ...optimistic } }));
        try {
            const res = await apiFetch(`/api/app-visibility`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ app_name: app, ...body }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Save failed");
            dnAlert.success("Updated", okMsg);
        } catch (e) {
            setApps((p) => ({ ...p, [app]: prev }));   // revert
            dnAlert.error("Couldn't update", e.message);
        } finally { setSavingApp(null); }
    };

    // Change the visibility mode (visitors / employees / hidden).
    const setVisibility = async (app, mode) => {
        const meta = APPS.find((a) => a.name === app);
        const current = apps[app]?.mode || (meta ? defaultVisFor(meta) : "employees");
        if (mode === current) return;

        if (mode === "visitors" && Array.isArray(meta?.roles)) {
            dnAlert.warning("Not applicable", `${app} is ${meta.roles.join(" / ")}-only in the portal - it can never be shown to visitors.`);
            return;
        }
        if (mode === "hidden") {
            const ok = await dnAlert.confirm({
                title: `Hide ${app} from everyone?`,
                message: `${app} will be removed from the launcher for everyone, including Admins, until you switch it back.`,
                confirmLabel: "Hide app", danger: true,
            });
            if (!ok) return;
        }
        patch(app, { mode }, { mode }, `${app} set to "${labelOf(mode)}".`);
    };

    // Toggle maintenance on/off - independent of the visibility mode.
    const setMaintenance = async (app, on) => {
        const current = !!apps[app]?.maintenance;
        if (on === current) return;

        if (on) {
            const ok = await dnAlert.confirm({
                title: `Put ${app} in maintenance?`,
                message: `${app} will show its maintenance page to anyone who opens it, until you switch it off. Its visibility setting is kept.`,
                confirmLabel: "Enable maintenance", danger: true,
            });
            if (!ok) return;
        }
        patch(app, { maintenance: on }, { maintenance: on }, `${app} maintenance ${on ? "enabled" : "turned off"}.`);
    };

    return (
        <div className="av-container">
            <div className={`av-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }} />
                        </a>
                        App Visibility
                    </h2>
                    <p className="dspr-subtitle">Control how each app is reachable - open to visitors, employee-only, or fully hidden. Maintenance is a separate switch that can be on together with any of those; it flips an nginx flag file so the maintenance page shows instantly without a rebuild.</p>
                </div>

                <div className="av-content">
                    <div className="av-legend">
                        <span className="av-legend-item"><FaGlobe /> <b>Visitors</b> - open to anyone, no login (only apps the portal shows to everyone).</span>
                        <span className="av-legend-item"><FaLock /> <b>Employee only</b> - logged-in employees only; visitors don't see the tile.</span>
                        <span className="av-legend-item"><FaEyeSlash /> <b>Hidden</b> - removed for everyone, including Admins.</span>
                        <span className="av-legend-item"><FaTools /> <b>Maintenance</b> - separate switch; can be on together with any mode. Serves the maintenance page (nginx flag).</span>
                    </div>

                    {loading ? (
                        <p className="loading-text">Loading...</p>
                    ) : (
                        <div className="av-grid">
                            {APPS.map((app) => {
                                const isPortal = !!app.maintenanceOnly;
                                const restricted = !isPortal && Array.isArray(app.roles);
                                const st = apps[app.name] || {};
                                const mode = st.mode || defaultVisFor(app);
                                const maint = !!st.maintenance;
                                const roleLabel = isPortal ? "The portal" : (restricted ? `${app.roles.join(" / ")} only` : "All roles");

                                // Availability = Live / Maintenance (Live means maintenance off).
                                const availBtns = [
                                    { value: "live",        label: "Live",        icon: FaPowerOff, active: !maint, onClick: () => setMaintenance(app.name, false), hint: "App is live and reachable." },
                                    { value: "maintenance", label: "Maintenance", icon: FaTools, active: maint,  onClick: () => setMaintenance(app.name, true),  hint: "Serves the maintenance page (nginx flag)." },
                                ];
                                const renderAvail = () => availBtns.map((b) => {
                                    const Icon = b.icon;
                                    return (
                                        <button key={b.value} type="button"
                                            className={`av-seg-opt ${b.active ? "active" : ""} av-${b.value}`}
                                            title={b.hint} disabled={savingApp === app.name} onClick={b.onClick}>
                                            <Icon /> {b.label}
                                        </button>
                                    );
                                });

                                // Visibility lead sentence for the card foot.
                                let visLead;
                                if (mode === "hidden") visLead = <><b>Hidden</b> - removed for everyone, including Admins.</>;
                                else if (mode === "visitors") visLead = <><b>Everyone</b> sees this tile - visitors and employees, no login needed.</>;
                                else visLead = <><b>Logged-in employees only.</b> Visitors don't see the tile at all.</>;

                                return (
                                    <div className="av-card" key={app.name} style={{ borderTopColor: app.color }}>
                                        <div className="av-card-head">
                                            <span className="av-app-dot" style={{ background: app.color }} />
                                            <span className="av-app-name">{app.name}</span>
                                            <span className={`av-role ${restricted ? "restricted" : ""} ${isPortal ? "portal" : ""}`}>
                                                {roleLabel}
                                            </span>
                                            {savingApp === app.name && <span className="av-saving">saving…</span>}
                                        </div>

                                        {isPortal ? (
                                            <div className="av-section">
                                                <div className="av-section-label">Portal status</div>
                                                <div className="av-seg av-seg-2">{renderAvail()}</div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="av-section">
                                                    <div className="av-section-label">Visibility</div>
                                                    <div className="av-seg av-seg-3">
                                                        {VIS_MODES.map((m) => {
                                                            const Icon = m.icon;
                                                            const active = mode === m.value;
                                                            const blocked = restricted && m.value === "visitors";
                                                            return (
                                                                <button key={m.value} type="button"
                                                                    className={`av-seg-opt ${active ? "active" : ""} av-${m.value}`}
                                                                    title={blocked ? `${app.name} is ${roleLabel} in the portal - visitors can never see it.` : m.hint}
                                                                    disabled={savingApp === app.name || blocked}
                                                                    onClick={() => setVisibility(app.name, m.value)}>
                                                                    <Icon /> {m.label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                {!app.noMaintenance && (
                                                    <div className="av-section">
                                                        <div className="av-section-label">Availability</div>
                                                        <div className="av-seg av-seg-2">{renderAvail()}</div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        <div className="av-hint">
                                            {isPortal ? (
                                                <p className="av-hint-main">
                                                    <b>{maint ? "Maintenance." : "Live."}</b>{" "}
                                                    {maint ? "The whole portal shows the maintenance page." : "The portal is reachable; each app below then follows its own mode."}
                                                </p>
                                            ) : (
                                                <>
                                                    <p className="av-hint-main">{visLead}</p>
                                                    {maint && <p className="av-hint-maint"><b>Maintenance is on</b> - the tile still shows, but opening it serves the maintenance page.</p>}
                                                    {restricted && <p className="av-hint-floor">Role floor: {roleLabel} - “Visitors” is disabled, the portal never shows this tile to a visitor.</p>}
                                                    {app.noMaintenance && <p className="av-hint-floor">Maintenance switch is disabled for the admin panel - it can't be taken offline from here (that would lock this page).</p>}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default App_visibility;
